import { useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { changePassword } from "../api/user";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import secureStorage from "../utils/secureStorage";

const ChangePassword = () => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleBack = () => {
    // Check if we came from Settings page
    const fromSettings = location.state?.from === 'settings' || 
                         document.referrer.includes('/settings');
    
    if (fromSettings) {
      navigate('/settings');
    } else {
      navigate('/home');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    // Validate passwords
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t("all_fields_are_required"));
      return;
    }

    if (newPassword === currentPassword) {
      toast.error(t("your_new_password_cannot_be_the_same_as_your_old_one"));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t("new_password_and_confirm_password_do_not_match"));
      return;
    }

    if (newPassword.length < 8) {
      toast.error(t("password_must_be_at_least_8_characters_long"));
      return;
    }

    try {
      // Call the API to change password
      const response = await changePassword({
        old_password: currentPassword,
        password: newPassword,
      });

      if (response.success) {
        setSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        secureStorage.removeItem("accessToken");
        navigate("/login");
      } else {
        setError(response.message || "Failed to change password");
      }
    } catch (error) {
      setError(error.message || "An error occurred while changing password");
    }
  };

  return (
    <>
      <CustomPageHeader
        // hideBack={true}
        // showPageHeader={false}
        pageContainerClass="px-2 w-full mx-w-4xl pt-5"
        title={t("Setting_page.change_password", "Vibelinks")}
        onBack={handleBack}
      >
        {/* Password change form */}
        <div className="bg-gray-100 rounded-xl p-4">
          {success && (
            <div className="bg-green-100 border border-green-400 text-[#6d29f6] px-4 py-3 rounded mb-4">
              {t(
                "change_password.success_message",
                "Password changed successfully!"
              )}
            </div>
          )}

          {error && (
            <div className="bg-red-100 border border-red-400 text-[#6d29f6] px-4 py-3 rounded mb-4">
              {t("change_password.error_message", error)}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="currentPassword"
              >
                {t("current_password")}
              </label>
              <div className="relative">
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-purple-500 focus:shadow-outline"
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  placeholder={t(
                    "enter_current_password",
                    "Enter your current password"
                  )}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  <FontAwesomeIcon
                    icon={showCurrentPassword ? faEyeSlash : faEye}
                    className="text-gray-500"
                  />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="newPassword"
              >
                {t("new_password")}
              </label>
              <div className="relative">
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-purple-500 focus:shadow-outline"
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  placeholder={t(
                    "enter_new_password",
                    "Enter your new password"
                  )}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  <FontAwesomeIcon
                    icon={showNewPassword ? faEyeSlash : faEye}
                    className="text-gray-500"
                  />
                </button>
              </div>
            </div>

            <div className="mb-6">
              <label
                className="block text-gray-700 text-sm font-bold mb-2"
                htmlFor="confirmPassword"
              >
                {t("confirm_new_password")}
              </label>
              <div className="relative">
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-purple-500 focus:shadow-outline"
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  placeholder={t(
                    "confirm_new_password",
                    "Confirm your new password"
                  )}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <FontAwesomeIcon
                    icon={showConfirmPassword ? faEyeSlash : faEye}
                    className="text-gray-500"
                  />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <button
                className="bg-[#6d29f6] hover:bg-purple-600 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline w-full"
                type="submit"
              >
                {t("change_password")}
              </button>
            </div>
          </form>
        </div>
      </CustomPageHeader>
    </>
  );
};

export default ChangePassword;
